import { ResponsiveContainer, Legend, YAxis } from 'recharts';
import { AreaChart, Area, CartesianGrid, Tooltip } from 'recharts';
import { XAxis } from 'recharts';
import { formatNumber, formatMinorUnits, formatMinorUnitsCompact } from 'helpers/formatters';
import { seriesColor } from '../Experiments/Details/ExperimentStatistics/control-options';

/**
 * Sessions outnumber conversions by two or three orders of magnitude, so a
 * single shared axis flattens everything except sessions onto the floor. Volume
 * and money also have no common unit. Three series, two axes: counts left,
 * revenue right.
 *
 * The two axes are a known compromise, not an endorsement: a reader cannot
 * compare a left-axis series against a right-axis one, because their crossings
 * are an artefact of the two scales. Splitting this into two charts stacked on a
 * shared date axis would be the honest fix.
 */
const COUNT_SERIES = ['Sessions', 'Conversions'] as const;
const REVENUE_SERIES = 'Revenue';

const SERIES_ORDER = [...COUNT_SERIES, REVENUE_SERIES];

/** Colour follows the series' identity, never its position in a filtered list. */
const colorFor = (name: string) => seriesColor(SERIES_ORDER.indexOf(name));

type DataPoint = {
  date: string;
  [key: string]: number | string;
};

type DashboardChartProps = {
  data: DataPoint[];
  /** Denomination of the Revenue series, which arrives in minor units. */
  currencyCode: string | null;
};

const AXIS_TICK = { fill: 'hsl(var(--dim))', fontSize: 10, fontFamily: 'var(--font-mono)' };

export function DashboardChart({ data, currencyCode }: DashboardChartProps) {
  return (
    <ResponsiveContainer width="100%" height={384}>
      <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
        <defs>
          {SERIES_ORDER.map((category) => (
            <linearGradient key={category} id={`color-${category}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={colorFor(category)} stopOpacity={0.22} />
              <stop offset="95%" stopColor={colorFor(category)} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        {/* Recessive grid: horizontal only, so the rules help read a value
            without competing with the series. */}
        <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="date" tick={AXIS_TICK} tickMargin={8} minTickGap={24} stroke="var(--line)" tickLine={false} />
        <YAxis
          yAxisId="count"
          width={56}
          tick={AXIS_TICK}
          stroke="var(--line)"
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => formatNumber(value)}
        />
        <YAxis
          yAxisId="revenue"
          orientation="right"
          width={72}
          tick={AXIS_TICK}
          stroke="var(--line)"
          tickLine={false}
          axisLine={false}
          tickFormatter={(value) => formatMinorUnitsCompact(value, currencyCode)}
        />
        <Tooltip
          cursor={{ stroke: 'var(--line)', strokeWidth: 1 }}
          contentStyle={{
            background: 'hsl(var(--panel))',
            border: '1px solid var(--line)',
            borderRadius: 12,
            fontSize: 12,
          }}
          labelStyle={{ color: 'hsl(var(--dim))', fontFamily: 'var(--font-mono)', fontSize: 10 }}
          itemStyle={{ color: 'hsl(var(--text))', fontSize: 12 }}
          formatter={(value, name) =>
            name === REVENUE_SERIES
              ? [formatMinorUnits(Number(value), currencyCode), name]
              : [formatNumber(Number(value)), name]
          }
        />
        {COUNT_SERIES.map((category) => (
          <Area
            key={category}
            yAxisId="count"
            type="monotone"
            dataKey={category}
            activeDot={{ r: 4, stroke: colorFor(category), strokeWidth: 2, fill: 'hsl(var(--panel))' }}
            stroke={colorFor(category)}
            fillOpacity={1}
            fill={`url(#color-${category})`}
            strokeWidth={2}
          />
        ))}
        <Area
          yAxisId="revenue"
          type="monotone"
          dataKey={REVENUE_SERIES}
          activeDot={{ r: 4, stroke: colorFor(REVENUE_SERIES), strokeWidth: 2, fill: 'hsl(var(--panel))' }}
          stroke={colorFor(REVENUE_SERIES)}
          fillOpacity={1}
          fill={`url(#color-${REVENUE_SERIES})`}
          strokeWidth={2}
        />
        <Legend wrapperStyle={{ fontSize: 11, color: 'hsl(var(--dim))' }} iconType="plainline" iconSize={12} />
      </AreaChart>
    </ResponsiveContainer>
  );
}
